import { Leaf, LockKeyhole, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ApiProblem, signIn } from "../lib/api";
import { RequiredPasswordChangePage } from "./RequiredPasswordChangePage";

export function LoginPage({ onSignedIn }: { readonly onSignedIn: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [rotationPassword, setRotationPassword] = useState<string>();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(undefined);
    try {
      const session = await signIn(email, password);
      if (session.password_change_required) {
        setRotationPassword(password);
        return;
      }
      onSignedIn();
    } catch (failure) {
      setError(
        failure instanceof ApiProblem
          ? failure.problem.detail
          : "Sign-in could not be completed. Check your connection and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (rotationPassword !== undefined) {
    return (
      <RequiredPasswordChangePage
        initialCurrentPassword={rotationPassword}
        onCompleted={() => {
          setRotationPassword(undefined);
          setPassword("");
          setNotice("Password changed. Sign in again with your new password.");
        }}
        onSignedOut={() => setRotationPassword(undefined)}
      />
    );
  }

  return (
    <main className="login-shell">
      <section className="login-context" aria-labelledby="login-heading">
        <div className="login-brand">
          <span className="brand-mark" aria-hidden="true">
            <Leaf />
          </span>
          <span>BalikTANiman</span>
        </div>
        <p className="login-kicker">Quezon Province staff console</p>
        <h1 id="login-heading">Keep every planting history connected.</h1>
        <p className="login-lead">
          Assign work, review field evidence, and follow verified growth from
          the first planting post onward.
        </p>
        <div className="login-assurance">
          <ShieldCheck aria-hidden="true" />
          <p>
            <strong>Application-protected access</strong>
            <span>
              Staff data stays behind identity, role, and geographic-scope
              checks.
            </span>
          </p>
        </div>
      </section>
      <section className="login-panel" aria-labelledby="sign-in-title">
        <div className="login-card">
          <span className="login-lock" aria-hidden="true">
            <LockKeyhole />
          </span>
          <h2 id="sign-in-title">Staff sign in</h2>
          <p>Use your BalikTANiman staff email and password.</p>
          {notice === undefined ? null : (
            <p className="login-notice" role="status">
              {notice}
            </p>
          )}
          <form onSubmit={(event) => void submit(event)}>
            <label htmlFor="staff-email">Email address</label>
            <input
              id="staff-email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <label htmlFor="staff-password">Password</label>
            <input
              id="staff-password"
              name="password"
              type="password"
              autoComplete="current-password"
              minLength={12}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            {error === undefined ? null : (
              <p className="login-error" role="alert">
                {error}
              </p>
            )}
            <button
              className="login-submit"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <p className="login-help">
            Access is limited to registered program staff.
          </p>
        </div>
      </section>
    </main>
  );
}
